import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { UseModalStateOptions } from "./useModalState";

const ModalContext = createContext<{
  setOpen: (open: boolean) => void;
  setModal: (args: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Modal: (props: any) => React.JSX.Element;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    props: Record<string, any>;
  }) => void;
} | null>(null);

export const ModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [{ Modal, props }, setModal] = useState<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Modal?: (props: Record<string, any>) => React.JSX.Element;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    props?: Record<string, any>;
  }>({});
  const [open, setOpen] = useState(false);

  const value = useMemo(() => {
    return {
      setOpen,
      setModal,
    };
  }, []);

  return (
    <ModalContext.Provider value={value}>
      {children}
      {Modal ? <Modal open={open} {...props} /> : null}
    </ModalContext.Provider>
  );
};

export interface UseModalOptions extends UseModalStateOptions {
  disableBackdrop?: boolean;
  /**
   * @default "md"
   */
  size?: "sm" | "md" | "lg";
}

// TODO: この実装はModalに入れる
const modalSizes = {
  sm: 400,
  md: 520,
  lg: 800,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useModal = <T extends Record<string, any>>(
  Modal: (props: T) => React.JSX.Element,
  options?: UseModalOptions,
) => {
  const ctx = useContext(ModalContext);
  if (ctx === null) {
    throw new Error("useModal must be used within a ModalProvider");
  }

  const handleClose = useCallback(() => {
    ctx.setOpen(false);
  }, [ctx]);

  const handleOpen = useCallback(
    (props?: T) => {
      ctx.setModal({
        Modal,
        props: {
          onClose: options?.disableClose ? undefined : handleClose,
          onClickOutside: options?.enableClickOutside ? handleClose : undefined,
          backdrop: options?.disableBackdrop ? false : true,
          maxWidth: modalSizes[options?.size ?? "md"],
          ...props,
        },
      });
      ctx.setOpen(true);
    },
    [Modal, options, handleClose, ctx],
  );
  return [handleOpen, handleClose] as const;
};
